import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { MedewerkersOpStationEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { MedewerkersOpStationDto, VosAanvraagPatch } from "@/shared/types";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { MEDEWERKERS_OP_STATION_AANVRAGEN_QUERY_KEY, MEDEWERKERS_OP_STATION_QUERY_KEY } from "../constants";
import { MedewerkersOpStation } from "../domain";

export function useGetMedewerkersOpStationAanvraag(dossierId: string) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, MEDEWERKERS_OP_STATION_AANVRAGEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<MedewerkersOpStationDto[]>(`/dossiers/${dossierId}/medewerkers`);

            return data ? data.map((dto) => MedewerkersOpStation.fromDto(dto)) : [];
        },
    });
}

export function useRealtimeMedewerkersOpStation() {
    const queryClient = useQueryClient();
    // Use a lightweight event instead of a payload-rich one, because the backend cannot reliably infer the event reason.
    useRealtimeTopic<MedewerkersOpStationEvent>("medewerkersOpStation", {
        onMessage: (message) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIERS_QUERY_KEY, String(message.dossierId), MEDEWERKERS_OP_STATION_AANVRAGEN_QUERY_KEY],
            });
        },
    });
}

export function useGetMedewerkersOpStations(stationCodes: string[]) {
    const apiClient = useApiClient();

    const results = useQueries({
        queries: stationCodes.map((code) => ({
            queryKey: [MEDEWERKERS_OP_STATION_QUERY_KEY, code],
            queryFn: async () => {
                const data = await apiClient.get<MedewerkersOpStationDto>(`/stations/${code}/medewerkers`);
                return data ? MedewerkersOpStation.fromDto(data) : null;
            },
        })),
    });
    const data = results.map((query) => query.data).filter((data) => data != null);
    const isPending = results.some((query) => query.isPending);
    const error = results.find((query) => query.isError)?.error;

    return { data, isPending, error };
}

type PostMedewerkersOpStationAanvraag = {
    dossierId: string;
    stationCodes: string[];
};

export function usePostMedewerkersOpStationAanvraag() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, stationCodes }: PostMedewerkersOpStationAanvraag) => {
            await apiClient.post(`/dossiers/${dossierId}/medewerkers`, stationCodes);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error posting medewerkers op station aanvraag:", error);
        },
    });
}

type PatchMedewerkersOpStationAanvraag = VosAanvraagPatch & {
    dossierNummer: string;
};

export function usePatchMedewerkersOpStation() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierNummer, ...data }: PatchMedewerkersOpStationAanvraag) => {
            await apiClient.patch(`/dossiers/${dossierNummer}/medewerkers`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierNummer);
        },
        onError: (error) => {
            console.error("Error patching medewerkers op station aanvraag:", error);
        },
    });
}
